import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { AnimatePresence, m } from 'motion/react';
import { Plus, Users } from 'lucide-react';

import { departments, employees, teams, type Team } from '../../api/organization';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useI18n, useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useSubmit } from '../auth/useSubmit';
import { useCompanyId } from '../company/useCompany';

const TYPES = ['functional', 'project', 'communication'] as const;

/**
 * `13 §3.1`: los equipos, su tipo y quién está en ellos.
 *
 * Los tres tipos no son decoración. Un equipo de comunicación es la puerta única
 * de `07` —el único sitio donde un empleado puede cruzar la frontera de la
 * empresa—, así que elegir uno aquí es una decisión de seguridad, y el selector
 * lo dice en vez de ofrecer tres palabras sin consecuencia.
 */
export function TeamsScreen() {
  const t = useT();
  const { plural } = useI18n();
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const { busy, error, fields, submit } = useSubmit<void>();

  const list = useQuery({
    queryKey: ['teams', companyId],
    queryFn: () => teams.list(companyId),
    enabled: companyId !== '',
  });

  const tree = useQuery({
    queryKey: ['departments', companyId],
    queryFn: () => departments.list(companyId),
    enabled: companyId !== '',
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['teams', companyId] });

  const archive = useMutation({
    mutationFn: (id: string) => teams.archive(id),
    onSuccess: () => void invalidate(),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const department = String(form.get('department_id') ?? '');

    void submit(
      async () => {
        await teams.create(companyId, {
          name: String(form.get('name') ?? ''),
          slug: String(form.get('slug') ?? ''),
          type: String(form.get('type') ?? 'functional'),
          purpose: String(form.get('purpose') ?? '') || null,
          department_id: department === '' ? null : department,
        });

        await invalidate();
      },
      () => setAdding(false),
    );
  };

  if (list.isPending) {
    return <SkeletonRows />;
  }

  if (list.error) {
    return <ErrorBanner error={list.error} onRetry={() => void list.refetch()} />;
  }

  return (
    <section className="space-y-6">
      <PageHeader
        title={t('org.teams.title')}
        description={t('org.teams.intro')}
        actions={
          !adding ? (
            <Button icon={<Plus />} onClick={() => setAdding(true)}>
              {t('org.teams.add')}
            </Button>
          ) : null
        }
      />

      {archive.error ? <ErrorBanner error={archive.error} /> : null}

      {adding ? (
        <m.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg space-y-4 rounded-lg border border-line bg-raised p-5 shadow-sm"
        >
          {error ? <ErrorBanner error={error} /> : null}

          <Field label={t('org.teams.name')} name="name" required error={fields['name']} />
          <Field
            label={t('org.teams.slug')}
            name="slug"
            required
            error={fields['slug']}
            hint={t('org.teams.slugHint')}
          />

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-ink">{t('org.teams.type')}</legend>
            {TYPES.map((type) => (
              <label key={type} className="flex cursor-pointer items-start gap-2.5 text-sm text-ink">
                <input
                  type="radio"
                  name="type"
                  value={type}
                  defaultChecked={type === 'functional'}
                  className="mt-1 size-4 accent-primary"
                />
                <span>
                  {t(`org.teams.types.${type}.label` as Key)}
                  <span className="block text-xs leading-relaxed text-ink-soft">
                    {t(`org.teams.types.${type}.hint` as Key)}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          <Field label={t('org.teams.purpose')} name="purpose" error={fields['purpose']} />

          <div className="space-y-1.5">
            <label htmlFor="department_id" className="block text-sm font-semibold text-ink">
              {t('org.teams.department')}
            </label>
            <select id="department_id" name="department_id" className={cn(fieldStyles, 'border-line')}>
              <option value="">{t('common.none')}</option>
              {(tree.data?.data ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <Button type="submit" busy={busy}>
              {t('common.create')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </m.form>
      ) : null}

      {list.data.data.length === 0 ? (
        <EmptyState icon={<Users />} title={t('org.teams.empty')} description={t('org.teams.emptyHint')} />
      ) : (
        <m.ul
          variants={STAGGER}
          initial="hidden"
          animate="shown"
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised"
        >
          {list.data.data.map((team) => (
            <m.li key={team.id} variants={RISE} className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                      {team.name}
                      {/* El equipo de comunicación es la única puerta al
                          exterior (`07`); merece que se vea en la lista. */}
                      {team.type === 'communication' ? (
                        <Badge tone="warning" size="sm">
                          {t('org.teams.types.communication.label')}
                        </Badge>
                      ) : null}
                    </p>
                    <p className="font-mono text-xs text-ink-faint">
                      {team.slug} · {team.type}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-ink-faint">
                  <span>{plural('org.teams.members', team.member_count)}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setExpanded(expanded === team.id ? null : team.id)}
                    aria-expanded={expanded === team.id}
                    aria-label={t('org.teams.membersLabel', { name: team.name })}
                  >
                    {t('org.teams.membersButton')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => archive.mutate(team.id)}
                    aria-label={t('org.teams.archiveLabel', { name: team.name })}
                  >
                    {t('common.archive')}
                  </Button>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {expanded === team.id ? (
                  <m.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.24, ease: [0.22, 0.8, 0.28, 1] }}
                    className="overflow-hidden"
                  >
                    <Members team={team} />
                  </m.div>
                ) : null}
              </AnimatePresence>
            </m.li>
          ))}
        </m.ul>
      )}
    </section>
  );
}

/**
 * Adding somebody already in the team **moves their role** rather than
 * failing — it is a double click or a change of mind. A second lead is refused,
 * because a team has one.
 */
function Members({ team }: { team: Team }) {
  const t = useT();
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const { busy, error, submit } = useSubmit<void>();

  const members = useQuery({ queryKey: ['team-members', team.id], queryFn: () => teams.members(team.id) });

  const roster = useQuery({
    queryKey: ['employees', companyId, 'roster'],
    queryFn: () => employees.list(companyId, new URLSearchParams({ limit: '100' })),
    enabled: companyId !== '',
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['team-members', team.id] });
    await queryClient.invalidateQueries({ queryKey: ['teams', companyId] });
  };

  const remove = useMutation({
    mutationFn: (employeeId: string) => teams.removeMember(team.id, employeeId),
    onSuccess: () => void refresh(),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    void submit(async () => {
      await teams.addMember(team.id, {
        employee_id: String(form.get('employee_id') ?? ''),
        role_in_team: String(form.get('role_in_team') ?? 'member'),
      });

      await refresh();
    });
  };

  return (
    <div className="mt-3 space-y-3 rounded-sm border border-line bg-muted p-3.5">
      {error ? <ErrorBanner error={error} /> : null}

      {members.data && members.data.data.length > 0 ? (
        <ul className="space-y-1 text-sm">
          {members.data.data.map((member) => (
            <li key={member.employee_id} className="flex items-center justify-between gap-3">
              <span className="text-ink">
                {member.name}
                <span className="text-ink-faint">
                  {' '}
                  · {t(`org.teams.roles.${member.role_in_team}` as Key)}
                </span>
              </span>
              <button
                type="button"
                className="text-xs font-semibold text-danger transition-colors hover:underline"
                onClick={() => remove.mutate(member.employee_id)}
                aria-label={t('org.teams.removeLabel', { name: member.name, team: team.name })}
              >
                {t('common.remove')}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-faint">{t('org.teams.nobodyYet')}</p>
      )}

      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label htmlFor={`member-${team.id}`} className="block text-xs font-semibold text-ink">
            {t('org.teams.addEmployee')}
          </label>
          <select
            id={`member-${team.id}`}
            name="employee_id"
            required
            className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
          >
            <option value="">{t('org.teams.choose')}</option>
            {(roster.data?.data ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label htmlFor={`role-${team.id}`} className="block text-xs font-semibold text-ink">
            {t('org.teams.role')}
          </label>
          <select
            id={`role-${team.id}`}
            name="role_in_team"
            className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
          >
            <option value="member">{t('org.teams.roles.member')}</option>
            <option value="lead">{t('org.teams.roles.lead')}</option>
          </select>
        </div>

        <Button type="submit" busy={busy}>
          {t('common.create')}
        </Button>
      </form>
    </div>
  );
}
